import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Skull, ArrowLeft, Loader2, Share2, Mail, Flame, Check, X } from "lucide-react";
import { toast } from "sonner";

// Local streak kept in localStorage; one play per date per device.
const STREAK_KEY = "wasntme_daily_streak";
const LAST_PLAYED_KEY = "wasntme_daily_last_played";
const LAST_RESULT_KEY = "wasntme_daily_last_result";

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const yesterdayLocal = () => {
  const d = new Date(); d.setDate(d.getDate() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const RESULT_META = {
  brilliant: { label: "Brilliant", sub: "Solved with 1 of 3 clues", color: "#d4af37" },
  sharp:     { label: "Sharp",     sub: "Solved with 2 of 3 clues", color: "#4ade80" },
  solved:    { label: "Solved",    sub: "Solved with 3 of 3 clues", color: "#f4f0ec" },
  cold:      { label: "Case Cold", sub: "Didn't crack it today",    color: "#ff6b6b" },
};

const Daily = () => {
  const [state, setState] = useState({ loading: true, puzzle: null, error: "" });
  // Step: 0 = setup+suspects, 1-3 = clue index to show, "guess" prompt after each clue
  const [visibleClues, setVisibleClues] = useState(0);
  const [guesses, setGuesses] = useState([]);  // {name, correct, afterClue}
  const [picked, setPicked] = useState("");
  const [reveal, setReveal] = useState(null);
  const [result, setResult] = useState(null);  // 'brilliant'|'sharp'|'solved'|'cold'
  const [streak, setStreak] = useState(() => parseInt(localStorage.getItem(STREAK_KEY) || "0", 10));
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);

  useEffect(() => {
    const t = todayLocal();
    api.get(`/daily/today?date_q=${t}`).then((r) => {
      setState({ loading: false, puzzle: r.data.puzzle, error: "" });
      api.post("/events", { event: "daily_played", props: { date: t } }).catch(() => {});
      const lastPlayed = localStorage.getItem(LAST_PLAYED_KEY);
      if (lastPlayed === r.data.puzzle.date) {
        const prev = localStorage.getItem(LAST_RESULT_KEY);
        if (prev) {
          setResult(prev);
          setAlreadyPlayed(true);
          api.get(`/daily/reveal?date_q=${r.data.puzzle.date}`).then((rr) => setReveal(rr.data)).catch(() => {});
        }
      }
    }).catch((err) => {
      setState({ loading: false, puzzle: null, error: err?.response?.data?.detail || "Puzzle unavailable." });
    });
  }, []);

  const puzzle = state.puzzle;

  const submitGuess = async () => {
    if (!picked) { toast.error("Pick a suspect."); return; }
    try {
      const { data } = await api.post("/daily/guess", { date: puzzle.date, guess: picked });
      const after = visibleClues;    // clues the player had seen when guessing
      setGuesses((g) => [...g, { name: picked, correct: data.correct, afterClue: after }]);
      setPicked("");
      if (data.correct) {
        const tier = after <= 1 ? "brilliant" : after === 2 ? "sharp" : "solved";
        finishRound(tier);
      } else if (visibleClues < 3) {
        // Reveal the next clue
        setVisibleClues((v) => v + 1);
        toast.message("Not quite. Here's the next clue.");
      } else {
        finishRound("cold");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit guess.");
    }
  };

  const skipToNextClue = () => {
    if (visibleClues < 3) setVisibleClues((v) => v + 1);
  };

  const finishRound = async (tier) => {
    setResult(tier);
    try {
      const { data } = await api.get(`/daily/reveal?date_q=${puzzle.date}`);
      setReveal(data);
    } catch { /* ignore */ }
    // Update streak
    const lastPlayed = localStorage.getItem(LAST_PLAYED_KEY) || "";
    const correct = tier !== "cold";
    let newStreak = streak;
    if (lastPlayed === yesterdayLocal() && correct) newStreak = streak + 1;
    else if (correct) newStreak = 1;
    else newStreak = 0;
    setStreak(newStreak);
    localStorage.setItem(STREAK_KEY, String(newStreak));
    localStorage.setItem(LAST_PLAYED_KEY, puzzle.date);
    localStorage.setItem(LAST_RESULT_KEY, tier);
    api.post("/daily/play", { date: puzzle.date, result: tier, streak: newStreak }).catch(() => {});
    api.post("/events", { event: "daily_result", props: { tier, streak: newStreak } }).catch(() => {});
  };

  const shareNative = async () => {
    const text = `wasntme.party Daily Whodunit #${puzzle.number} · ${(RESULT_META[result] || {}).label}${streak > 0 ? ` · Streak: ${streak}` : ""}. No spoilers.`;
    const url = `${window.location.origin}/daily`;
    if (navigator.share) {
      try { await navigator.share({ title: "Daily Whodunit", text, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Copied — paste anywhere.");
    }
    api.post("/events", { event: "daily_share_clicked" }).catch(() => {});
  };

  if (state.loading) {
    return <div className="grain min-h-screen flex items-center justify-center text-[#a39797]"><Loader2 className="animate-spin" /></div>;
  }
  if (!puzzle) {
    return (
      <div className="grain min-h-screen flex items-center justify-center p-6">
        <div className="card-noir p-6 max-w-md" data-testid="daily-error">
          <div className="text-[#ff6b6b] text-sm mb-3">{state.error}</div>
          <Link to="/" className="btn-ghost text-sm"><ArrowLeft size={14} /> back</Link>
        </div>
      </div>
    );
  }

  const difficultyColor = { Easy: "#4ade80", Medium: "#d4af37", Hard: "#ff6b6b" }[puzzle.difficulty] || "#a39797";

  return (
    <div className="grain min-h-screen" data-testid="daily-page">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">daily whodunit · #{String(puzzle.number).padStart(3, "0")}</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-2">{puzzle.title}</h1>
        <div className="flex items-center gap-3 mb-6 text-sm">
          <span style={{ color: difficultyColor }}>{puzzle.difficulty}</span>
          <span className="text-[#a39797]">·</span>
          <span className="text-[#a39797] inline-flex items-center gap-1">
            <Flame size={14} className={streak > 0 ? "text-[#d4af37]" : ""} /> streak: {streak}
          </span>
        </div>

        {/* The case */}
        <div className="card-noir p-5 mb-6">
          <div className="text-xs tracking-widest text-[#a39797] mb-2">THE CASE</div>
          <p className="text-[#c7bebe] leading-relaxed">{puzzle.setup}</p>
        </div>

        {/* Suspects */}
        <div className="mb-6">
          <div className="text-xs tracking-widest text-[#a39797] mb-3">SUSPECTS</div>
          <div className="grid md:grid-cols-3 gap-3">
            {puzzle.suspects.map((s) => (
              <div key={s.name} className="card-noir p-3" data-testid={`daily-suspect-${s.name}`}>
                <div className="font-display text-lg">{s.name}</div>
                <div className="text-xs text-[#a39797]">{s.profile}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Clues — progressively revealed */}
        <div className="mb-6">
          <div className="text-xs tracking-widest text-[#a39797] mb-3">CLUES</div>
          <div className="space-y-2">
            {puzzle.clues.map((c, i) => (
              <div key={i}
                data-testid={`daily-clue-${i}`}
                className={`card-noir p-4 ${i < visibleClues + 1 ? "" : "opacity-30 border-dashed"}`}>
                <div className="flex items-start gap-3">
                  <div className="font-display text-xl text-[#8b0000]">{i + 1}.</div>
                  <div className={i < visibleClues + 1 ? "text-[#f4f0ec]" : "text-[#a39797] italic"}>
                    {i < visibleClues + 1 ? c : "Reveal to continue."}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Guess or Result */}
        {!result ? (
          <div className="card-noir p-5 border-[#8b0000]">
            <div className="text-xs tracking-widest text-[#a39797] mb-3">
              YOUR GUESS AFTER CLUE {visibleClues + 1}
              <span className="font-hand text-[#d4af37] ml-3">earlier guesses score higher</span>
            </div>
            <div className="grid md:grid-cols-3 gap-2 mb-3">
              {puzzle.suspects.map((s) => (
                <button key={s.name} onClick={() => setPicked(s.name)}
                  data-testid={`daily-pick-${s.name}`}
                  className={`card-noir p-3 transition-colors ${picked === s.name ? "border-[#8b0000] bg-[#8b0000]/10" : "hover:border-[#8b0000]"}`}>
                  <div className="font-display text-lg">{s.name}</div>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={submitGuess} disabled={!picked}
                className="btn-primary disabled:opacity-50" data-testid="daily-submit">
                <Skull size={14} /> lock it in
              </button>
              {visibleClues < 2 && (
                <button onClick={skipToNextClue} className="btn-ghost text-sm" data-testid="daily-skip">
                  skip to clue {visibleClues + 2}
                </button>
              )}
            </div>
            {guesses.length > 0 && (
              <div className="mt-4 text-xs text-[#a39797]">
                Previous: {guesses.map((g, i) => (
                  <span key={i} className="mr-2">
                    {g.correct ? <Check size={12} className="inline text-[#4ade80]" /> : <X size={12} className="inline text-[#ff6b6b]" />}
                    &nbsp;{g.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="card-noir p-6" style={{ borderColor: RESULT_META[result].color }} data-testid="daily-result">
            <div className="font-hand text-[#d4af37] rotate-[-1deg] text-xl mb-1">your verdict</div>
            <div className="font-display text-4xl mb-1" style={{ color: RESULT_META[result].color }}>
              {RESULT_META[result].label}
            </div>
            <div className="text-sm text-[#a39797] mb-5">{RESULT_META[result].sub}</div>
            {reveal && (
              <>
                <div className="text-xs tracking-widest text-[#a39797] mb-1">THE CULPRIT</div>
                <div className="font-display text-2xl mb-2 text-[#8b0000]">{reveal.culprit}</div>
                <p className="text-[#c7bebe] leading-relaxed mb-5">{reveal.explanation}</p>
              </>
            )}
            {streak > 0 && (
              <div className="text-sm mb-4 inline-flex items-center gap-2 text-[#d4af37]">
                <Flame size={14} /> streak × {streak}
              </div>
            )}
            {alreadyPlayed && (
              <div className="text-xs text-[#a39797] mb-3">You already played today's puzzle. Come back tomorrow.</div>
            )}
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={shareNative} className="btn-primary text-sm" data-testid="daily-share">
                <Share2 size={14} /> Share spoiler-free
              </button>
              <a href={`/api/daily/share-card.png?date_q=${puzzle.date}&result=${result}&streak=${streak}`}
                target="_blank" rel="noreferrer" className="btn-ghost text-sm" data-testid="daily-download">
                Download card
              </a>
            </div>
            <div className="text-xs text-[#a39797] mt-5 flex items-center gap-2">
              <Mail size={12} /> Want tomorrow's puzzle in your inbox? <Link to="/#join" className="underline ml-1">Join the list</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Daily;
